import React, { useMemo, useState, useCallback } from 'react';
import { TreeNode } from '../types';

export type GitStatusInfo = {
  icon: string;
  color: string;
};

type TreeViewProps = {
  tree: TreeNode[];
  collapsed: Record<string, boolean>;
  activeFile: string | null;
  selectedNodeId: string | null;
  multiSelectedIds?: Set<string>;
  isDirty: (path: string) => boolean;
  gitStatus?: Record<string, GitStatusInfo>;
  onSelect: (id: string, event?: React.MouseEvent) => void;
  onOpen: (path: string) => void;
  onToggle: (path: string) => void;
  onContextMenu?: (event: React.MouseEvent, node: TreeNode) => void;
  onMove?: (sourceId: string, targetId: string) => void;
  height?: number;
};

const TreeNodeRow = React.memo(function TreeNodeRow({
  node,
  depth,
  isCollapsed,
  isActive,
  isSelected,
  isMultiSelected,
  dirty,
  isDragOver,
  gitStatus,
  onClick,
  onToggle,
  onContextMenu,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  node: TreeNode;
  depth: number;
  isCollapsed: boolean;
  isActive: boolean;
  isSelected: boolean;
  isMultiSelected: boolean;
  dirty: boolean;
  isDragOver: boolean;
  gitStatus?: GitStatusInfo;
  onClick: (e: React.MouseEvent) => void;
  onToggle: () => void;
  onContextMenu?: (event: React.MouseEvent, node: TreeNode) => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  return (
    <div
      role="treeitem"
      aria-label={`${node.type === 'folder' ? 'Folder' : 'File'}: ${node.name}${dirty ? ', unsaved changes' : ''}${gitStatus ? ', modified' : ''}`}
      aria-expanded={node.type === 'folder' ? !isCollapsed : undefined}
      aria-selected={isSelected || isMultiSelected}
      aria-level={depth + 1}
      tabIndex={isSelected ? 0 : -1}
      className={[
        'tree-row',
        node.type,
        isActive ? 'active' : '',
        isSelected ? 'selected' : '',
        isMultiSelected ? 'multi-selected' : '',
        isDragOver ? 'drag-over' : '',
        gitStatus ? 'git-changed' : '',
      ].join(' ').trim()}
      style={{ paddingLeft: `${depth * 14}px` }}
      draggable
      onClick={(e) => {
        onClick(e);
        if (node.type === 'folder' && !e.metaKey && !e.shiftKey) onToggle();
      }}
      onContextMenu={(e) => {
        if (onContextMenu) onContextMenu(e, node);
      }}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {node.type === 'folder' && <span className={`caret ${isCollapsed ? 'collapsed' : ''}`} aria-hidden="true" />}
      <span className="node-icon" aria-hidden="true">
        {node.type === 'folder' ? '📁' : '📄'}
      </span>
      <span className="node-name" style={gitStatus ? { color: gitStatus.color } : undefined}>{node.name}</span>
      {gitStatus && <span className="node-badge git-status" style={{ color: gitStatus.color }} aria-hidden="true">{gitStatus.icon}</span>}
      {dirty && <span className="node-badge dirty" aria-hidden="true" title="Unsaved changes">●</span>}
    </div>
  );
});

export const TreeView = React.memo(function TreeView({
  tree,
  collapsed,
  activeFile,
  selectedNodeId,
  multiSelectedIds,
  isDirty,
  gitStatus,
  onSelect,
  onOpen,
  onToggle,
  onContextMenu,
  onMove,
  height = 360,
}: TreeViewProps) {
  const rowHeight = 32;
  const overscan = 8;
  const [scrollTop, setScrollTop] = useState(0);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const flatNodes = useMemo(() => {
    const list: Array<{ node: TreeNode; depth: number }> = [];
    const walk = (nodes: TreeNode[], depth: number) => {
      nodes.forEach((node) => {
        list.push({ node, depth });
        const isCollapsed = node.type === 'folder' ? !!collapsed[node.id] : false;
        if (node.children && !isCollapsed) walk(node.children, depth + 1);
      });
    };
    walk(tree, 0);
    return list;
  }, [collapsed, tree]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const totalHeight = flatNodes.length * rowHeight;
  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const endIndex = Math.min(flatNodes.length, Math.ceil((scrollTop + height) / rowHeight) + overscan);
  const visible = flatNodes.slice(startIndex, endIndex);

  const handleDragStart = useCallback((e: React.DragEvent, nodeId: string) => {
    e.dataTransfer.setData('text/plain', nodeId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(nodeId);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, nodeId: string, nodeType: string) => {
    e.preventDefault();
    e.stopPropagation();
    // Only allow dropping on folders or at root level
    if (nodeType === 'folder' && nodeId !== draggedId) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverId(nodeId);
    }
  }, [draggedId]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string, targetType: string) => {
    e.preventDefault();
    e.stopPropagation();
    const sourceId = e.dataTransfer.getData('text/plain');

    if (sourceId && targetId !== sourceId && targetType === 'folder' && onMove) {
      onMove(sourceId, targetId);
    }

    setDraggedId(null);
    setDragOverId(null);
  }, [onMove]);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  const Row = ({ item, index }: { item: { node: TreeNode; depth: number }; index: number }) => {
    const { node, depth } = item;
    const isActive = node.id === activeFile;
    const isSelected = node.id === selectedNodeId;
    const isMultiSelected = multiSelectedIds?.has(node.id) ?? false;
    const dirty = node.type === 'file' && isDirty(node.id);
    const isCollapsed = node.type === 'folder' ? !!collapsed[node.id] : false;
    const isDragOver = dragOverId === node.id;
    const nodeGitStatus = gitStatus?.[node.id];

    return (
      <div
        style={{ position: 'absolute', top: (startIndex + index) * rowHeight, left: 0, right: 0, height: rowHeight }}
        onDragEnd={handleDragEnd}
      >
        <TreeNodeRow
          node={node}
          depth={depth}
          isCollapsed={isCollapsed}
          isActive={isActive}
          isSelected={isSelected}
          isMultiSelected={isMultiSelected}
          dirty={dirty}
          isDragOver={isDragOver}
          gitStatus={nodeGitStatus}
          onClick={(e: React.MouseEvent) => {
            onSelect(node.id, e);
            if (node.type === 'file' && !e.metaKey && !e.shiftKey) onOpen(node.id);
          }}
          onToggle={() => onToggle(node.id)}
          onContextMenu={onContextMenu}
          onDragStart={(e) => handleDragStart(e, node.id)}
          onDragOver={(e) => handleDragOver(e, node.id, node.type)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, node.id, node.type)}
        />
      </div>
    );
  };

  return (
    <div
      role="tree"
      aria-label="Project file tree"
      className="tree-virtual"
      style={{ position: 'relative', height, overflow: 'auto' }}
      onScroll={handleScroll}
    >
      <div role="group" style={{ position: 'relative', height: totalHeight }}>
        {visible.map((item, idx) => (
          <Row key={item.node.id} item={item} index={idx} />
        ))}
      </div>
    </div>
  );
});
